import { IAppContext } from 'App/AppContext';
import { ExpressionValidationState } from 'components/KPI/Expression/expressionEditor.helpers';
import KPI, { KPITypes } from 'models/kpi/kpi';
import StringValue from 'models/stringValue/stringValue';
import Tag from 'models/tag';
import { TFunction } from 'i18next';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiCreateTag } from 'services/Api/tagService';
import { apiRequest } from 'services/Auth/authConfig';
import { isEmpty } from 'utils/string';

export const applyPickType = (kpi: KPI, type: KPITypes, t: TFunction<string[]>): KPI => {
  const newKPI = kpi.clone();
  newKPI.resetTypeConfig();
  newKPI.type = type;
  if (type === KPITypes.SuccesError) {
    newKPI.defValue = t('kpis:Fields.QuestionExample');
  }
  if (type === KPITypes.Expression) {
    newKPI.isField = false;
  }

  return newKPI;
};

export const addCreatedTags = async (tags: Tag[], appContext: IAppContext): Promise<Tag[]> => {
  try {
    const accessToken = await appContext.getAccessToken(apiRequest.scopes);
    const tagsToCreate = tags.filter((tag) => tag.tagId < 0);
    const newTags = await Promise.all(tagsToCreate.map((tag) => apiCreateTag(tag, accessToken)));
    newTags.forEach((newTag) => appContext.globalDataCache.tags.add(newTag));

    return newTags;
  } catch (err) {
    appContext.setError(err);

    return [];
  }
};

interface UseKPIFieldUpdatersParams {
  kpi: KPI;
  onChange: (kpi: KPI) => void;
  tags: Tag[];
  onTagsChange: (tags: Tag[]) => void;
}

export const useKPIFieldUpdaters = (params: UseKPIFieldUpdatersParams) => {
  const { kpi, onChange, tags, onTagsChange } = params;
  const { t } = useTranslation(['translation', 'kpis']);

  const defValueUpdated = (defValue: string | undefined) => {
    const newKpi = kpi.clone();
    newKpi.defValue = defValue;
    if (newKpi.type === KPITypes.Expression) {
      newKpi.data[0].resultNumber = KPI.calcPreviewExpression(defValue);
    }
    onChange(newKpi);
  };

  const minValueUpdated = (minValue: string | undefined) => {
    const newKpi = kpi.clone();
    newKpi.minValue = minValue;
    onChange(newKpi);
  };

  const maxValueUpdated = (maxValue: string | undefined) => {
    const newKpi = kpi.clone();
    newKpi.maxValue = maxValue;
    onChange(newKpi);
  };

  const requiredUpdated = (required: boolean) => {
    const newKpi = kpi.clone();
    newKpi.required = required;
    onChange(newKpi);
  };

  const showLabelUpdated = (showLabel: boolean) => {
    const newKpi = kpi.clone();
    newKpi.showLabel = showLabel;
    // Same guard the question editor applies on its own onChange: without a label the API
    // requires a question (DefValue), so hiding the label with an empty question re-seeds
    // the example instead of letting the draft become unsaveable.
    if (!showLabel && newKpi.type === KPITypes.SuccesError && isEmpty(newKpi.defValue)) {
      newKpi.defValue = t('kpis:Fields.QuestionExample');
    }
    onChange(newKpi);
  };

  const choicesUpdated = (choices: StringValue[] | undefined, defvalue: string | undefined) => {
    const newKpi = kpi.clone();
    newKpi.defValue = defvalue;
    newKpi.choices = choices;
    onChange(newKpi);
  };

  const decimalCountUpdated = (decimalCount: number | undefined) => {
    const newKpi = kpi.clone();
    newKpi.decimalCount = decimalCount;

    if (kpi.type === KPITypes.Number) {
      if (newKpi.defValue) {
        const nr = Number.parseFloat(newKpi.defValue);
        newKpi.defValue = nr.toFixed(decimalCount);
      }
      if (newKpi.minValue) {
        const nr = Number.parseFloat(newKpi.minValue);
        newKpi.minValue = nr.toFixed(decimalCount);
      }
      if (newKpi.maxValue) {
        const nr = Number.parseFloat(newKpi.maxValue);
        newKpi.maxValue = nr.toFixed(decimalCount);
      }
    }

    onChange(newKpi);
  };

  const setList = (listId: number | undefined, folderId: string | undefined) => {
    const newKPI = kpi.clone();
    newKPI.listId = listId;
    newKPI.folderId = folderId;
    onChange(newKPI);
  };

  const onAddTag = (tag: Tag) => {
    const newTags = [...tags, tag];
    onTagsChange(newTags);
    const newKpi = kpi.clone();
    newKpi.tagIds = newTags.map((item) => item.tagId);
    onChange(newKpi);
  };

  const onCreateTag = (tag: Tag) => {
    onAddTag(tag);
  };

  const onRemoveTag = (tag: Tag) => {
    const newTags = tags.filter((item) => item.tagId !== tag.tagId);
    onTagsChange(newTags);
    const newKpi = kpi.clone();
    newKpi.tagIds = newTags.map((item) => item.tagId);
    onChange(newKpi);
  };

  return {
    defValueUpdated,
    minValueUpdated,
    maxValueUpdated,
    requiredUpdated,
    showLabelUpdated,
    choicesUpdated,
    decimalCountUpdated,
    setList,
    onAddTag,
    onCreateTag,
    onRemoveTag,
  };
};

export const usePreviewDebounce = (
  kpi: KPI,
  isOpen: boolean,
  isLoading: boolean,
  updatePreviewKPI?: (kpi: KPI) => void,
) => {
  const timerRef = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!updatePreviewKPI || !isOpen || isLoading) return;

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => updatePreviewKPI(kpi.clone()), 200);
  }, [kpi, isOpen, isLoading, updatePreviewKPI]);
};

// Save must not be blocked by a legacy formula the user never touched; it gates only
// new or edited expressions until they validate.
export const useExpressionSaveGate = (current: KPI, original: KPI) => {
  const [state, setState] = useState<ExpressionValidationState>('pending');

  const blocksSave =
    current.type === KPITypes.Expression &&
    (original.type !== KPITypes.Expression ||
      (current.defValue ?? '') !== (original.defValue ?? '')) &&
    state !== 'valid';

  const originalExpression = original.type === KPITypes.Expression ? (original.defValue ?? '') : '';

  return {
    blocksSave,
    originalExpression,
    onValidationStateChange: setState,
    reset: () => setState('pending'),
    state,
  };
};

// The tracking card only earns its space when it offers an action: promote for fields,
// demote for unmanaged KPIs edited from a form. Managed KPIs must stay KPIs, and the
// Organization KPI page is delete-only, so neither gets a card.
export const showTrackingCard = (isField: boolean, isManaged: boolean, inFormContext: boolean): boolean =>
  isField || (!isManaged && inFormContext);

// Formula operands: saved Number fields/KPIs, scoped to the current form when a form context is
// given (the Organization KPI page passes none and keeps the full pool), never the field itself.
export const filterFormulaOperands = (
  kpis: KPI[] | undefined,
  formKpiIds: number[] | undefined,
  selfKpiId: number,
): KPI[] => {
  const saved = (kpis ?? []).filter(
    (k) => k.type === KPITypes.Number && k.kpiId > 0 && k.kpiId !== selfKpiId,
  );
  if (!formKpiIds) return saved;

  const idsOnForm = new Set(formKpiIds);

  return saved.filter((k) => idsOnForm.has(k.kpiId));
};
